import { useEffect, useMemo } from "react";
import MVT from "ol/format/MVT";
import type { StyleLike } from "ol/style/Style";
import { useParams } from "react-router-dom";
import {
  State,
  VectorTile as VectorTileSource,
} from "../../../../../reactOL/source";
import VectorTile from "../../../../../reactOL/layers/VectorTile";
import { useViewContext } from "../../../../../../context/View/ViewContext";
import { useAuthContext } from "../../../../../auth/AuthContext";

interface ViewOrderItemVectorTileLayerProps {
  visible: boolean;
  style: StyleLike;
  queryParams?: URLSearchParams;
}

const buildViewOrderItemTileUrl = (
  layerName: string,
  orderItemId: string,
  queryParams?: URLSearchParams,
) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL ?? "/api";
  const normalizedBaseUrl =
    apiBaseUrl.endsWith("/") ? apiBaseUrl : `${apiBaseUrl}/`;
  const queryString = queryParams?.toString();

  return `${normalizedBaseUrl}ViewOrderItem/${encodeURIComponent(layerName)}/${orderItemId}/tiles/{z}/{x}/{y}${queryString ? `?${queryString}` : ""}`;
};

const ViewOrderItemVectorTileLayer = ({
  visible,
  style,
  queryParams,
}: ViewOrderItemVectorTileLayerProps) => {
  const { projection, tileGrid, setLoading } = useViewContext();
  const { user } = useAuthContext();
  const { layer, orderItemId } = useParams();
  const layerName = layer ? decodeURIComponent(layer) : "";

  const authorizationToken = useMemo(() => {
    if (!user?.token) {
      return null;
    }

    return `Bearer ${user.token}`;
  }, [user?.token]);

  const orderItemTileSource = useMemo(
    () =>
      VectorTileSource({
        url: buildViewOrderItemTileUrl(
          layerName || "unknown",
          "00000000-0000-0000-0000-000000000000",
        ),
        onStateChange: (state: State) => setLoading(state === State.Loading),
        authorizationToken,
        projection,
        format: new MVT(),
        tileGrid,
      }),
    [authorizationToken, layerName, projection, setLoading, tileGrid],
  );

  useEffect(() => {
    if (!visible || !orderItemId || !layerName) {
      setLoading(false);
      return;
    }

    orderItemTileSource.setUrl(
      buildViewOrderItemTileUrl(layerName, orderItemId, queryParams),
    );
  }, [
    layerName,
    orderItemId,
    orderItemTileSource,
    queryParams,
    setLoading,
    visible,
  ]);

  return (
    <VectorTile
      source={orderItemTileSource}
      style={style}
      zIndex={9500}
      visible={visible}
    />
  );
};

export default ViewOrderItemVectorTileLayer;
